import { useSyncExternalStore } from 'react';

/**
 * 테넌트(조직) 슬러그 스토어 (CR-013).
 * 우선순위: URL ?tenant= → localStorage(prism.tenantSlug) → VITE_DEV_TENANT_SLUG.
 * 로그인 화면에서 조직 ID 를 직접 입력하면 여기 저장되고, 이후 모든 API 요청의 X-Tenant-Slug 로 쓰인다.
 */
const KEY = 'prism.tenantSlug';
const RECENT_KEY = 'prism.recentTenants';
const RECENT_MAX = 3;

/** 슬러그 규칙: 영문 소문자·숫자·하이픈, 2~63자, 하이픈으로 시작·끝나지 않음 */
export const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])$/;
export function normalizeSlug(raw: string) { return raw.trim().toLowerCase(); }
export function isValidSlug(raw: string) { return SLUG_RE.test(normalizeSlug(raw)); }

function read(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function write(key: string, value: string | null) { try { if (value === null) localStorage.removeItem(key); else localStorage.setItem(key, value); } catch { /* ignore */ } }

/** CR-067: 조직 주소로 들어왔는가(?tenant= 또는 커스텀 도메인). 아니면 로그인은 이메일 우선 흐름 */
let explicit = false;
let current: string | undefined = resolveInitial();
/** CR-040: 검증된 커스텀 도메인으로 접속하면 호스트가 조직을 확정한다 — 조직 변경 불가 */
let hostLocked = false;
const listeners = new Set<() => void>();
function emit() { listeners.forEach((l) => l()); }
export function lockTenantToHost(slug: string) { hostLocked = true; current = normalizeSlug(slug); write(KEY, current); emit(); }
export function isTenantHostLocked() { return hostLocked; }
export function isTenantExplicit() { return explicit || hostLocked; }

function resolveInitial(): string | undefined {
  const fromQuery = new URLSearchParams(location.search).get('tenant');
  if (fromQuery) { explicit = true; write(KEY, fromQuery); pushRecent(fromQuery); return fromQuery; }
  return read(KEY) ?? import.meta.env.VITE_DEV_TENANT_SLUG ?? undefined;
}

/** 현재 슬러그 (비반응형). API 클라이언트가 헤더를 만들 때 쓴다. */
export function tenantSlug(): string | undefined { return current; }

/** 현재 슬러그 (반응형). 로그인 화면이 조직 입력 단계를 보일지 결정할 때 쓴다. */
export function useTenantSlug(): string | undefined {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => current, () => current);
}

export function setTenantSlug(raw: string) {
  const slug = normalizeSlug(raw);
  current = slug; write(KEY, slug); pushRecent(slug); emit();
}

/** 저장된 조직을 지우고 조직 입력 단계로 돌아간다. 개발용 기본값(VITE_DEV_TENANT_SLUG)도 무시한다. */
export function clearTenantSlug() { if (hostLocked) return; current = undefined; write(KEY, null); emit(); }

export function recentTenants(): string[] {
  try { const v = JSON.parse(read(RECENT_KEY) ?? '[]'); return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []; } catch { return []; }
}
function pushRecent(slug: string) {
  const next = [slug, ...recentTenants().filter((s) => s !== slug)].slice(0, RECENT_MAX);
  write(RECENT_KEY, JSON.stringify(next));
}
